import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle, AlertTriangle, Clock, Brain, RefreshCw,
  ThumbsUp, ThumbsDown, BarChart3, Loader2, Eye, MessageSquare,
  Share2, Bookmark, MousePointer, Send, Check, X, Edit3
} from 'lucide-react';
import CommentSection from '@/components/social/CommentSection';
import VariantsPanel from '@/components/social/VariantsPanel';
import PublishPanel from '@/components/social/PublishPanel';

const API = '/api';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-600' },
  under_review: { label: 'Under Review', color: 'bg-blue-100 text-blue-700' },
  pending_admin_review: { label: 'Pending Admin Review', color: 'bg-purple-100 text-purple-700' },
  changes_requested: { label: 'Changes Needed', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-600' },
};

const SEVERITY_COLOR = {
  high: 'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-blue-50 text-blue-700 border-blue-200',
};

export default function ContentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [adminMode, setAdminMode] = useState(null); // null | 'accept_ai' | 'custom' | 'reject'
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState({}); // { index: true/false }
  const [includeCaption, setIncludeCaption] = useState(true);
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [analytics, setAnalytics] = useState({
    impressions: '', reach: '', likes: '', comments: '', shares: '', link_clicks: '', saves: '', notes: ''
  });

  const isAdmin = user?.role === 'Admin';
  const isCreator = user?.user_id === content?.user_id;

  const fetchContent = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/social/content/${id}`, { withCredentials: true });
      setContent(data);
    } catch (e) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContent(); }, [id]);

  const triggerAiReview = async () => {
    setReviewLoading(true);
    try {
      await axios.post(`${API}/social/content/${id}/ai-review`, {}, { withCredentials: true });
      toast.success('AI review complete');
      fetchContent();
    } catch (e) {
      toast.error('AI review failed');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleAdminAction = async (action, notes) => {
    setActionLoading(action);
    try {
      if (action === 'approve') {
        await axios.post(`${API}/social/content/${id}/approve`, { notes }, { withCredentials: true });
        toast.success('Content approved!');
      } else if (action === 'request-changes') {
        await axios.post(`${API}/social/content/${id}/request-changes`, { notes }, { withCredentials: true });
        toast.success('Changes sent to user');
      } else if (action === 'reject') {
        await axios.post(`${API}/social/content/${id}/reject`, { reason: notes }, { withCredentials: true });
        toast.success('Content rejected');
      }
      setAdminMode(null);
      setAdminNotes('');
      fetchContent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading('');
    }
  };

  const handleAcceptAiAndSend = () => {
    const review = content?.latest_ai_review;
    if (!review) return;
    const parts = [];
    const pickedIssues = (review.issues || []).filter((_, i) => selectedIssues[i]);
    if (pickedIssues.length > 0) {
      parts.push('AI-identified issues to fix:');
      pickedIssues.forEach((issue, i) => {
        parts.push(`${i + 1}. [${issue.severity?.toUpperCase()}] ${issue.issue}${issue.suggestion ? ` → ${issue.suggestion}` : ''}`);
      });
    }
    if (includeCaption && review.suggested_caption) {
      parts.push(`\nSuggested caption:\n${review.suggested_caption}`);
    }
    if (includeHashtags && review.suggested_hashtags) {
      parts.push(`\nSuggested hashtags: ${review.suggested_hashtags}`);
    }
    if (adminNotes.trim()) {
      parts.push(`\nAdmin note: ${adminNotes.trim()}`);
    }
    if (parts.length === 0) {
      toast.error('Select at least one change to send');
      return;
    }
    const fullNotes = parts.join('\n');
    handleAdminAction('request-changes', fullNotes);
  };

  // Initialize issue selection when entering accept_ai mode
  const enterAcceptAiMode = () => {
    const review = content?.latest_ai_review;
    if (review?.issues) {
      const init = {};
      review.issues.forEach((_, i) => { init[i] = true; });
      setSelectedIssues(init);
    }
    setIncludeCaption(true);
    setIncludeHashtags(true);
    setAdminMode('accept_ai');
  };

  const submitAnalytics = async () => {
    try {
      await axios.post(`${API}/social/content/${id}/analytics`, {
        impressions: Number(analytics.impressions) || 0,
        reach: Number(analytics.reach) || 0,
        likes: Number(analytics.likes) || 0,
        comments: Number(analytics.comments) || 0,
        shares: Number(analytics.shares) || 0,
        link_clicks: Number(analytics.link_clicks) || 0,
        saves: Number(analytics.saves) || 0,
        notes: analytics.notes,
      }, { withCredentials: true });
      toast.success('Analytics submitted!');
      setShowAnalytics(false);
      fetchContent();
    } catch (e) {
      toast.error('Failed to submit analytics');
    }
  };

  if (loading) return (
    <div className="max-w-4xl mx-auto p-6 space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64" />
      <Skeleton className="h-48" />
    </div>
  );

  if (!content) return (
    <div className="max-w-4xl mx-auto p-6">
      <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
      <p className="text-center text-slate-400 mt-12">Content not found</p>
    </div>
  );

  const review = content.latest_ai_review;
  const statusCfg = STATUS_CONFIG[content.status] || {};
  const canAdminAct = isAdmin && (content.status === 'under_review' || content.status === 'pending_admin_review' || content.status === 'changes_requested' || content.status === 'draft');

  return (
    <div className="max-w-4xl mx-auto space-y-5 p-4 md:p-6" data-testid="content-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/social')} className="text-slate-500">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-slate-900" data-testid="content-title">
              {content.blog_title || content.caption?.slice(0, 50) || 'Untitled Post'}
            </h1>
            <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            by {content.user_name} &middot; v{content.revision_number} &middot; {content.content_type}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={triggerAiReview} disabled={reviewLoading} data-testid="ai-review-btn">
          {reviewLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Brain className="h-4 w-4 mr-1" />}
          {review ? 'Re-run AI Review' : 'Run AI Review'}
        </Button>
      </div>

      {/* User Notice: Changes Requested */}
      {content.status === 'changes_requested' && isCreator && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="changes-notice">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Changes Requested</p>
              <p className="text-sm text-amber-700 mt-1">Please review the feedback below, make the changes, and re-submit your content.</p>
              {content.admin_notes?.length > 0 && (
                <div className="mt-3 bg-white rounded-lg p-3 border border-amber-200">
                  <p className="text-xs font-medium text-slate-400 mb-1">Latest Feedback</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{content.admin_notes[content.admin_notes.length - 1]?.notes}</p>
                </div>
              )}
              <Button size="sm" className="mt-3" onClick={() => navigate('/social/upload', { state: { editContentId: id } })} data-testid="revise-btn">
                <Edit3 className="h-3.5 w-3.5 mr-1" /> Revise & Re-submit
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Content Preview */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Content</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-1 mb-2">
            {(content.platforms || []).map(p => (
              <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
            ))}
          </div>

          {/* Image / Video Preview */}
          {(content.file?.url || content.file?.storage_path) && (
            <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50" data-testid="content-media-preview">
              {content.content_type === 'video' ? (
                <video
                  src={content.file.url || `/api/social/media/${content.file.storage_path}`}
                  controls
                  className="w-full max-h-96 object-contain bg-black"
                  data-testid="content-video"
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={content.file.url || `/api/social/media/${content.file.storage_path}`}
                  alt={content.caption || 'Content image'}
                  className="w-full max-h-96 object-contain"
                  data-testid="content-image"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              )}
              {content.file.original_filename && (
                <div className="px-3 py-1.5 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[10px] text-slate-400 truncate">{content.file.original_filename}</span>
                  <a href={content.file.url || `/api/social/media/${content.file.storage_path}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:text-blue-700 shrink-0 ml-2">
                    Open full size
                  </a>
                </div>
              )}
            </div>
          )}

          {content.caption && (
            <div>
              <Label className="text-xs text-slate-400">Caption</Label>
              <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{content.caption}</p>
            </div>
          )}
          {content.hashtags && (
            <div>
              <Label className="text-xs text-slate-400">Hashtags</Label>
              <p className="text-sm text-blue-600 mt-1">{content.hashtags}</p>
            </div>
          )}
          {content.blog_content && (
            <div>
              <Label className="text-xs text-slate-400">Blog Content</Label>
              <div className="text-sm text-slate-700 whitespace-pre-wrap mt-1 max-h-64 overflow-y-auto p-3 bg-slate-50 rounded-lg">
                {content.blog_content}
              </div>
            </div>
          )}
          {content.purpose && <p className="text-xs text-slate-400">Purpose: {content.purpose}</p>}
          {content.target_audience && <p className="text-xs text-slate-400">Audience: {content.target_audience}</p>}
        </CardContent>
      </Card>

      {/* AI Review */}
      {review && (
        <Card className="border-slate-200" data-testid="ai-review-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-violet-500" />
                <CardTitle className="text-base">AI Review</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={review.recommendation === 'approve' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                  {review.recommendation === 'approve' ? 'Recommends Approve' : 'Recommends Revise'}
                </Badge>
                <span className={`text-2xl font-bold ${review.score >= 70 ? 'text-emerald-600' : review.score >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                  {review.score}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">{review.summary}</p>

            {review.strengths?.length > 0 && (
              <div>
                <Label className="text-xs text-emerald-600">Strengths</Label>
                <ul className="mt-1 space-y-1">
                  {review.strengths.map((s, i) => (
                    <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {review.issues?.length > 0 && (
              <div>
                <Label className="text-xs text-red-600">Issues Found</Label>
                <div className="mt-1 space-y-2">
                  {review.issues.map((issue, i) => (
                    <div key={i} className={`p-3 rounded-lg border ${SEVERITY_COLOR[issue.severity] || SEVERITY_COLOR.low}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-[10px]">{issue.severity}</Badge>
                        <span className="text-xs font-medium">{issue.category}</span>
                      </div>
                      <p className="text-sm">{issue.issue}</p>
                      {issue.suggestion && <p className="text-xs mt-1 opacity-80">Suggested fix: {issue.suggestion}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {review.suggested_caption && (
              <div className="p-3 bg-violet-50 rounded-lg border border-violet-100">
                <Label className="text-xs text-violet-600">Suggested Caption</Label>
                <p className="text-sm text-violet-900 mt-1 whitespace-pre-wrap">{review.suggested_caption}</p>
              </div>
            )}
            {review.suggested_hashtags && (
              <div className="p-3 bg-violet-50 rounded-lg border border-violet-100">
                <Label className="text-xs text-violet-600">Suggested Hashtags</Label>
                <p className="text-sm text-violet-900 mt-1">{review.suggested_hashtags}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Admin Decision Panel */}
      {canAdminAct && (
        <Card className="border-slate-200 border-l-4 border-l-indigo-500" data-testid="admin-actions">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Admin Decision</CardTitle>
            <p className="text-xs text-slate-500 mt-1">Review the content and AI suggestions, then decide</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Decision buttons — shown when no mode selected */}
            {!adminMode && (
              <div className="space-y-3">
                {review && review.recommendation !== 'approve' && review.issues?.length > 0 && (
                  <button
                    onClick={enterAcceptAiMode}
                    className="w-full flex items-start gap-3 p-3 rounded-lg border-2 border-violet-200 bg-violet-50 hover:bg-violet-100 text-left transition-colors"
                    data-testid="accept-ai-btn"
                  >
                    <Brain className="h-5 w-5 text-violet-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-sm text-violet-800">Accept AI Suggestions & Send to User</p>
                      <p className="text-xs text-violet-600 mt-0.5">Forward AI-identified issues and suggestions to the creator for revision</p>
                    </div>
                  </button>
                )}
                <button
                  onClick={() => setAdminMode('custom')}
                  className="w-full flex items-start gap-3 p-3 rounded-lg border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 text-left transition-colors"
                  data-testid="custom-changes-btn"
                >
                  <Edit3 className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-sm text-blue-800">Request Custom Changes</p>
                    <p className="text-xs text-blue-600 mt-0.5">Write your own feedback for the creator</p>
                  </div>
                </button>
                <Separator />
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAdminAction('approve', '')}
                    disabled={!!actionLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                    data-testid="approve-btn"
                  >
                    {actionLoading === 'approve' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ThumbsUp className="h-4 w-4 mr-1" />}
                    Approve as-is
                  </Button>
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700 border-red-200"
                    onClick={() => setAdminMode('reject')}
                    disabled={!!actionLoading}
                    data-testid="reject-mode-btn"
                  >
                    <ThumbsDown className="h-4 w-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            )}

            {/* Accept AI mode — shows summary + optional admin note */}
            {adminMode === 'accept_ai' && (
              <div className="space-y-3" data-testid="accept-ai-panel">
                <p className="text-xs font-medium text-violet-600">Select which AI changes to send to the user:</p>
                
                {/* Issue checkboxes */}
                {review?.issues?.map((issue, i) => (
                  <label key={i} className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${selectedIssues[i] ? 'bg-violet-50 border-violet-300' : 'bg-slate-50 border-slate-200 opacity-60'}`}>
                    <input
                      type="checkbox"
                      checked={!!selectedIssues[i]}
                      onChange={e => setSelectedIssues(prev => ({ ...prev, [i]: e.target.checked }))}
                      className="mt-0.5 rounded"
                      data-testid={`issue-check-${i}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className="text-[10px]">{issue.severity}</Badge>
                        <span className="text-xs font-medium text-slate-600">{issue.category}</span>
                      </div>
                      <p className="text-xs text-slate-700 mt-0.5">{issue.issue}</p>
                      {issue.suggestion && <p className="text-xs text-violet-600 mt-0.5">Fix: {issue.suggestion}</p>}
                    </div>
                  </label>
                ))}

                {/* Caption toggle */}
                {review?.suggested_caption && (
                  <label className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${includeCaption ? 'bg-violet-50 border-violet-300' : 'bg-slate-50 border-slate-200 opacity-60'}`}>
                    <input type="checkbox" checked={includeCaption} onChange={e => setIncludeCaption(e.target.checked)} className="mt-0.5 rounded" data-testid="include-caption-check" />
                    <div>
                      <p className="text-xs font-medium text-violet-700">Include suggested caption</p>
                      <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{review.suggested_caption}</p>
                    </div>
                  </label>
                )}

                {/* Hashtags toggle */}
                {review?.suggested_hashtags && (
                  <label className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${includeHashtags ? 'bg-violet-50 border-violet-300' : 'bg-slate-50 border-slate-200 opacity-60'}`}>
                    <input type="checkbox" checked={includeHashtags} onChange={e => setIncludeHashtags(e.target.checked)} className="mt-0.5 rounded" data-testid="include-hashtags-check" />
                    <div>
                      <p className="text-xs font-medium text-violet-700">Include suggested hashtags</p>
                      <p className="text-xs text-blue-600 mt-0.5 line-clamp-1">{review.suggested_hashtags}</p>
                    </div>
                  </label>
                )}

                <div className="space-y-1">
                  <Label className="text-xs">Add your own note (optional)</Label>
                  <Textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Anything extra..." rows={2} data-testid="admin-note-ai" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleAcceptAiAndSend} disabled={!!actionLoading} className="bg-violet-600 hover:bg-violet-700" data-testid="send-ai-changes-btn">
                    {actionLoading === 'request-changes' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                    Send Selected Changes
                  </Button>
                  <Button variant="ghost" onClick={() => { setAdminMode(null); setAdminNotes(''); }}>Cancel</Button>
                </div>
              </div>
            )}

            {/* Custom changes mode */}
            {adminMode === 'custom' && (
              <div className="space-y-3" data-testid="custom-changes-panel">
                <div className="space-y-1">
                  <Label>Your feedback for the creator</Label>
                  <Textarea
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    placeholder="Describe what changes you want the user to make..."
                    rows={4}
                    data-testid="admin-custom-notes"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAdminAction('request-changes', adminNotes)}
                    disabled={!!actionLoading || !adminNotes.trim()}
                    data-testid="send-custom-changes-btn"
                  >
                    {actionLoading === 'request-changes' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                    Send to User
                  </Button>
                  <Button variant="ghost" onClick={() => { setAdminMode(null); setAdminNotes(''); }}>Cancel</Button>
                </div>
              </div>
            )}

            {/* Reject mode */}
            {adminMode === 'reject' && (
              <div className="space-y-3" data-testid="reject-panel">
                <div className="space-y-1">
                  <Label>Reason for rejection</Label>
                  <Textarea
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    placeholder="Explain why this content is being rejected..."
                    rows={3}
                    data-testid="admin-reject-notes"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    onClick={() => handleAdminAction('reject', adminNotes)}
                    disabled={!!actionLoading || !adminNotes.trim()}
                    data-testid="confirm-reject-btn"
                  >
                    {actionLoading === 'reject' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ThumbsDown className="h-4 w-4 mr-1" />}
                    Confirm Reject
                  </Button>
                  <Button variant="ghost" onClick={() => { setAdminMode(null); setAdminNotes(''); }}>Cancel</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Publish to Platforms (for approved content) */}
      {content.status === 'approved' && (
        <PublishPanel content={content} onPublished={fetchContent} />
      )}

      {/* Analytics Input (for approved posts) */}
      {content.status === 'approved' && (
        <Card className="border-slate-200" data-testid="analytics-section">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-500" /> Post Analytics
              </CardTitle>
              {!showAnalytics && (
                <Button variant="outline" size="sm" onClick={() => setShowAnalytics(true)} data-testid="add-analytics-btn">
                  Add Analytics
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {content.analytics_entries?.length > 0 && (
              <div className="mb-4 space-y-2">
                {content.analytics_entries.map((entry, i) => (
                  <div key={i} className="grid grid-cols-4 md:grid-cols-7 gap-3 p-3 bg-slate-50 rounded-lg text-center">
                    {[
                      { icon: Eye, label: 'Impr.', val: entry.impressions },
                      { icon: Eye, label: 'Reach', val: entry.reach },
                      { icon: ThumbsUp, label: 'Likes', val: entry.likes },
                      { icon: MessageSquare, label: 'Comments', val: entry.comments },
                      { icon: Share2, label: 'Shares', val: entry.shares },
                      { icon: MousePointer, label: 'Clicks', val: entry.link_clicks },
                      { icon: Bookmark, label: 'Saves', val: entry.saves },
                    ].map(m => (
                      <div key={m.label}>
                        <m.icon className="h-3 w-3 mx-auto text-slate-400" />
                        <p className="text-lg font-bold text-slate-800">{(m.val || 0).toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400">{m.label}</p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {showAnalytics && (
              <div className="space-y-4 border-t pt-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['impressions', 'reach', 'likes', 'comments', 'shares', 'link_clicks', 'saves'].map(field => (
                    <div key={field} className="space-y-1">
                      <Label className="text-xs capitalize">{field.replace('_', ' ')}</Label>
                      <Input
                        type="number"
                        value={analytics[field]}
                        onChange={e => setAnalytics(prev => ({ ...prev, [field]: e.target.value }))}
                        placeholder="0"
                        data-testid={`analytics-${field}`}
                      />
                    </div>
                  ))}
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Notes</Label>
                    <Input
                      value={analytics.notes}
                      onChange={e => setAnalytics(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any observations..."
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={submitAnalytics} data-testid="submit-analytics-btn">
                    <BarChart3 className="h-4 w-4 mr-1" /> Submit Analytics
                  </Button>
                  <Button variant="ghost" onClick={() => setShowAnalytics(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Change History */}
      {content.admin_notes?.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Feedback History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {content.admin_notes.map((note, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.notes}</p>
                  <p className="text-xs text-slate-400 mt-2">{note.requested_at?.split('T')[0]} &middot; by {note.requested_by || 'Admin'}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* A/B Variants */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <VariantsPanel
            contentId={content.content_id}
            existingVariants={content.variants || []}
            selectedVariant={content.selected_variant}
            onVariantSelected={() => fetchContent()}
          />
        </CardContent>
      </Card>

      {/* Comments */}
      <Card className="border-slate-200">
        <CardContent className="pt-6">
          <CommentSection contentId={content.content_id} />
        </CardContent>
      </Card>
    </div>
  );
}
